import { ExplainActionDialog } from "@/components/ExplainAction/ExplainActionDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { UserToolSet } from "@cohesive/api-client";
import { Boxes, ChevronRight, ScanText, Terminal } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { CommandBlock } from "./CommandBlock";

/**
 * The pending-confirmation card for a sandbox terminal command, framed as a
 * request to operate the agent's sandboxed computer: a titlebar-style header names the
 * surface ("Agent Computer") and carries the terminal mark, the body leads with
 * the plain-language `actionDescription` (what the agent wants to do) plus the
 * connected apps the command touches, and the raw command tucks behind a "Show
 * command" toggle. The `footer` slot carries the Accept/Cancel buttons (pending)
 * or the command's output (executed).
 *
 * `relative z-[1]` lifts the whole card above the activity rail's connector line
 * (an absolutely-positioned sibling that otherwise paints through this
 * static-flow card and strikes a hairline across it).
 */
export function TerminalActionCard({
  command,
  actionDescription,
  matchedToolSets,
  toolCallId,
  footer,
}: {
  command: string;
  actionDescription?: string;
  matchedToolSets: UserToolSet[];
  /** Stable id used to cache the action explanation. Omit to hide "Explain". */
  toolCallId?: string;
  footer?: ReactNode;
}) {
  const [showCommand, setShowCommand] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);

  // Descriptions arrive as lowercase gerund phrases ("duplicating the zip…");
  // capitalize so the line reads as the agent's status narration.
  const raw = actionDescription?.trim();
  const description = raw
    ? raw.charAt(0).toUpperCase() + raw.slice(1)
    : "Run a command on the agent's computer";

  const explainInput = useMemo(
    () =>
      buildToolCallExplainInput({
        command,
        actionDescription,
        matchedToolSets,
      }),
    [command, actionDescription, matchedToolSets],
  );

  return (
    <div className="relative z-[1] -mb-1 mt-5 overflow-hidden rounded-xl border bg-card">
      {/* Titlebar — names the surface (the agent's sandboxed computer) and
          frames the card as a permission request, with the terminal mark set
          large in the corner. */}
      <div className="flex items-start justify-between gap-3 border-b bg-muted/30 px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground">
            Agent Computer
          </span>
          <span className="text-xs text-muted-foreground">
            Requesting permission to run a command
          </span>
        </div>
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground"
        >
          <Terminal className="size-5" />
        </span>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm leading-relaxed text-foreground">{description}</p>

        {matchedToolSets.length > 0 && (
          <ConnectorRow toolSets={matchedToolSets} />
        )}

        {/* Learn-more affordances: read the raw command, or get a plain-language
            explanation of what it will actually do. */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowCommand((v) => !v);
            }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-expanded={showCommand}
          >
            <ChevronRight
              size={14}
              className={cn("transition-transform", showCommand && "rotate-90")}
            />
            {showCommand ? "Hide command" : "Show command"}
          </button>

          {toolCallId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setExplainOpen(true);
              }}
            >
              <ScanText />
              Explain this action
            </Button>
          )}
        </div>

        {showCommand && (
          <div className="rounded-lg border overflow-hidden">
            <CommandBlock command={command} />
          </div>
        )}

        {footer && <div className="pt-1">{footer}</div>}
      </div>

      {toolCallId && (
        <ExplainActionDialog
          open={explainOpen}
          onOpenChange={setExplainOpen}
          cacheKey={toolCallId}
          inputText={explainInput}
        />
      )}
    </div>
  );
}

/**
 * Composes the single `input_text` prompt for the explain-action endpoint (it
 * takes no separate system field, so the role, task, command, and connector
 * context all live in one string). The model is instructed to stay grounded in
 * the command and never invent recipients/filenames/destinations.
 */
function buildToolCallExplainInput({
  command,
  actionDescription,
  matchedToolSets,
}: {
  command: string;
  actionDescription?: string;
  matchedToolSets: UserToolSet[];
}): string {
  const apps = matchedToolSets
    .map((ts) => ts.display_name || ts.name)
    .filter(Boolean);

  const lines = [
    "An AI agent wants to run the shell command below on the user's sandboxed computer.",
    "The user is deciding whether to approve it and may not read shell fluently.",
    "Explain in plain language what will actually happen if they approve.",
    "",
    "Command:",
    "```sh",
    command,
    "```",
  ];

  const intent = actionDescription?.trim();
  if (intent) lines.push("", `The agent's stated intent: ${intent}`);
  if (apps.length > 0) {
    lines.push(
      "",
      `Connected apps this command may access: ${apps.join(", ")}.`,
    );
  }

  lines.push(
    "",
    "Ground every detail strictly in the command: name real recipients, filenames, URLs, and destinations only when they appear in it, and never invent any. If the command only reads or inspects (changing nothing outside the sandbox), say so and mark it read-only. Keep each step to one short sentence.",
  );

  return lines.join("\n");
}

/**
 * The command references connected-app CLIs, so running it can reach real data
 * in those services — the card's most load-bearing caution. A contained scope
 * panel (mirroring the titlebar band) states that plainly, then lists each app
 * as a chip. Chips sit on `bg-background` so their logos read against the
 * panel's muted fill.
 */
function ConnectorRow({ toolSets }: { toolSets: UserToolSet[] }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Boxes className="size-3.5 shrink-0" />
        Heads up! This command may access the following connected apps
      </div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {toolSets.map((ts) => {
          const imageURL = ts.integration?.metadata?.image_url;
          const label = ts.display_name || ts.name;
          return (
            <Tooltip key={ts.id}>
              <TooltipTrigger asChild>
                {/* Design-system chip: h-7 (28px) with 12px right padding; the
                    avatar's left inset (pl-1) lands its centre on the pill's
                    left arc so the logo nests cleanly instead of reading as
                    clipped. bg-card lifts the chip a rung above the panel. */}
                <span className="inline-flex h-7 items-center gap-1.5 rounded-full border bg-card pl-1 pr-3 text-xs font-medium text-foreground">
                  <Avatar className="size-5">
                    {imageURL && <AvatarImage src={imageURL} alt={label} />}
                    <AvatarFallback className="text-[10px]">
                      {label.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent>Accesses your connected {label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
